import React, { type ReactNode } from 'react';
import { Box, type BoxProps } from '@biome-design-system/ui/Box';
import { useHistory } from 'react-router-dom';
import type { PMCConfig } from '@kyruus/types';

import type {
  GetUpdatedSearch,
  Log,
  SearchModification,
  SearchSummary as SearchSummaryType
} from 'Src/types';
import { SearchSummary } from '../SearchSummary';
import { Sort, type SortProps } from './Sort';

interface BaseResultsHeaderProps
  extends Pick<BoxProps, 'sx'>,
    Pick<SortProps, 'sortOptions'> {
  log: Log;
  config: PMCConfig;
}

type ResultsHeaderWithSummaryProps = BaseResultsHeaderProps & {
  getUpdatedSearch: GetUpdatedSearch;
  heading?: never;
  isLoadingOverlayShown?: boolean;
  locationName?: string;
  onSortChange?: never;
  totalProviders: number;
  searchSummary: SearchSummaryType;
  sort?: never;
};

type ResultsHeaderWithoutSummaryProps = BaseResultsHeaderProps & {
  getUpdatedSearch?: never;
  heading?: ReactNode;
  isLoadingOverlayShown?: never;
  locationName?: never;
  onSortChange: (value: string) => void;
  totalProviders?: never;
  searchSummary?: never;
  sort: string;
};

type ResultsHeaderProps =
  | ResultsHeaderWithSummaryProps
  | ResultsHeaderWithoutSummaryProps;

export const ResultsHeader = ({
  config,
  totalProviders,
  searchSummary,
  sortOptions,
  sort,
  getUpdatedSearch,
  onSortChange,
  sx,
  locationName,
  heading,
  log,
  isLoadingOverlayShown = false
}: ResultsHeaderProps) => {
  const history = useHistory();
  const handleSortChange = (value: string) => {
    if (getUpdatedSearch) {
      const mods: SearchModification[] = value
        ? [{ action: 'append', key: 'sort', value }]
        : [{ action: 'delete_key', key: 'sort' }];
      history.push(getUpdatedSearch(mods));
    } else {
      onSortChange(value);
    }
  };

  return (
    <Box
      sx={{
        ...sx,
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        justifyContent: 'space-between',
        width: '100%'
      }}
    >
      {getUpdatedSearch ? (
        <SearchSummary
          totalProviders={totalProviders}
          searchSummary={searchSummary}
          getUpdatedSearch={getUpdatedSearch}
          locationName={locationName}
          log={log}
          isLoadingOverlayShown={isLoadingOverlayShown}
        />
      ) : null}

      {heading}

      {sortOptions.length > 0 && (
        <Sort
          config={config}
          sort={searchSummary ? searchSummary.sort : sort}
          sortOptions={sortOptions}
          onSortChange={handleSortChange}
          disabled={totalProviders === 0}
        />
      )}
    </Box>
  );
};
